// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import './ProgressBar.css';

/**
 * Reusable Progress Bar Component
 * Provides consistent progress visualization across the app
 * 
 * @param {number} value - Current value (0-max)
 * @param {number} max - Maximum value (default: 100)
 * @param {string} color - Color mode: 'auto', 'success', 'warning', 'critical', 'info', or custom hex
 * @param {boolean} showLabel - Show percentage label
 * @param {string} label - Custom label text (overrides percentage)
 * @param {boolean} animated - Animate the fill
 * @param {boolean} striped - Show striped pattern
 * @param {string} size - Size: 'small', 'medium', 'large'
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 */
const ProgressBar = ({
  value = 0,
  max = 100,
  color = 'auto',
  showLabel = true,
  label = null,
  animated = false,
  striped = false,
  size = 'medium',
  className = '',
  style = {}
}) => {
  // Calculate percentage and clamp between 0-100
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  // Ensure we never get NaN or Infinity
  const safePercentage = isNaN(percentage) || !isFinite(percentage) ? 0 : percentage;

  // Determine color based on percentage if auto
  const getColor = () => {
    if (color !== 'auto') {
      // Predefined colors
      const colorMap = {
        success: '#00ff88',
        warning: '#ffaa00',
        critical: '#ff4444',
        info: '#00ffff',
        primary: '#00ffff',
        secondary: '#ff00ff'
      };
      return colorMap[color] || color; // Return mapped color or custom hex
    }

    // Auto color based on percentage
    if (percentage >= 80) return '#00ff88'; // Green
    if (percentage >= 60) return '#ffaa00'; // Orange
    if (percentage >= 40) return '#ff8800'; // Dark orange
    return '#ff4444'; // Red
  };

  const fillColor = getColor();

  // Generate label text using safe percentage
  const labelText = label !== null ? label : `${Math.round(safePercentage)}%`;

  const containerClasses = [
    'progress-bar-container',
    `progress-bar-${size}`,
    className
  ].filter(Boolean).join(' ');

  const fillClasses = [
    'progress-bar-fill',
    animated && 'progress-bar-animated',
    striped && 'progress-bar-striped'
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} style={style}>
      <div className="progress-bar-track">
        <div
          className={fillClasses}
          style={{
            width: `${safePercentage}%`,
            background: fillColor,
            boxShadow: `0 0 10px ${fillColor}80`
          }}
        >
          {showLabel && (
            <span className="progress-bar-label">
              {labelText}
            </span>
          )}
        </div>
      </div>
      {showLabel && safePercentage < 20 && (
        <span className="progress-bar-label-outside" style={{ color: fillColor }}>
          {labelText}
        </span>
      )}
    </div>
  );
};

export default ProgressBar;
